import { Link } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Sun, Clock, ChevronRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/services";
import { CITIES, cityPath, servicePath } from "../data/cities";

const HERO_IMG = "https://images.unsplash.com/photo-1584921465673-50f761092bae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHw0fHxzZWN1cml0eSUyMGd1YXJkJTIwcHJvZmVzc2lvbmFsJTIwcGF0cm9sJTIwbmlnaHQlMjBwcm90ZWN0aW9ufGVufDB8fHx8MTc5MjM3NDUyNXww&ixlib=rb-4.1.0&q=85";
const CITY_IMG = "https://images.unsplash.com/photo-1543962226-818f4301073f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx0b3JvbnRvJTIwc2t5bGluZSUyMG5pZ2h0JTIwY2l0eSUyMG9udGFyaW98ZW58MHx8fHwxNzkyMzc0NTMxfDA&ixlib=rb-4.1.0&q=85";

const homeFaqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What security guard services does 1st Nations Security provide in Ontario?",
      acceptedAnswer: { "@type": "Answer", text: "1st Nations Security provides uniformed security guards, mobile patrol security, construction site security, event security and crowd management, retail loss prevention, concierge and condominium security, industrial and mining security, healthcare security, 24/7 fire watch, alarm response, executive protection, Indigenous community security, K9 security and airport/transit security across Ontario." },
    },
    {
      "@type": "Question",
      name: "Which Ontario cities does 1st Nations Security serve?",
      acceptedAnswer: { "@type": "Answer", text: "We serve cities and communities across all of Ontario, including Thunder Bay, Toronto, Ottawa, Mississauga, Brampton, Hamilton, London, Kitchener-Waterloo, Windsor, Barrie, Kingston, Sudbury, North Bay, Sault Ste. Marie, Timmins, Kenora, Dryden, Fort Frances, Sioux Lookout, Red Lake and many more." },
    },
    {
      "@type": "Question",
      name: "How do I hire security guards in Ontario?",
      acceptedAnswer: { "@type": "Answer", text: "Call our 24/7 dispatch at +1 807-770-0243 or email admin@1stnation-security.com. Tell us your location, site type and coverage needs and we will build a security plan for you." },
    },
  ],
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Security Guard Services in Ontario | Toronto, Ottawa, Thunder Bay | 1st Nations Security"
        description="Security guards, mobile patrols, construction and event security across Ontario. Request coverage for Toronto, Ottawa, Thunder Bay and beyond."
        path="/"
        jsonLd={homeFaqLd}
      />

      <section data-testid="ccib-badge-section" className="pt-24 pb-2 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-white px-6 py-4 sm:px-10 sm:py-6 shadow-lg shadow-amber-500/5">
          <img
            src="/ccib-member.png"
            alt="A proud member of the Canadian Council for Indigenous Business — CCIB Certified Indigenous Business"
            data-testid="ccib-member-badge"
            className="mx-auto h-16 sm:h-20 w-auto object-contain"
          />
        </div>
      </section>

      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="1st Nations Security professional security guard on patrol in Ontario" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/80 to-slate-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-4">100% Indigenous-Owned · Ontario Security Services</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight text-white max-w-4xl font-heading">
            Security guard services <span className="text-amber-400">across Ontario</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            1st Nations Security provides licensed, professional security guards, mobile patrol, construction site
            security, event security, fire watch and alarm response to businesses and communities in every region of
            Ontario — from our Northwestern Ontario home to the GTA, Ottawa and Windsor. From security guards in
            Toronto and security guards in Thunder Bay to construction security across Ontario, our 24/7 dispatch
            covers the whole province.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="tel:+18077700243" data-testid="hero-call-button" className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-slate-950 transition-all duration-200 hover:bg-amber-400 hover:scale-[1.02]">
              Call +1 807-770-0243
            </a>
            <Link to="/security-services" data-testid="hero-services-button" className="inline-flex items-center gap-2 rounded-full border border-amber-500/40 px-7 py-3.5 text-sm font-semibold text-amber-300 transition-colors duration-200 hover:bg-amber-500/10">
              Explore Our Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl">
            {[
              { icon: ShieldCheck, label: "Licensed Ontario Guards" },
              { icon: Sun, label: "100% Indigenous-Owned" },
              { icon: Clock, label: "24/7 Dispatch & Response" },
              { icon: MapPin, label: `${CITIES.length}+ Ontario Cities` },
            ].map((b) => (
              <div key={b.label} data-testid={`hero-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <b.icon className="h-5 w-5 text-amber-400 shrink-0" />
                <span className="text-xs sm:text-sm font-medium text-slate-200">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="services-overview-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">Ontario Security Services</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-heading mb-4">
          Security Guard Services for Every Ontario Industry
        </h2>
        <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-10">
          From uniformed security guards and mobile patrol security to construction site security, retail loss
          prevention, healthcare security and 24/7 fire watch, our licensed Ontario security guards protect what
          matters most to your business.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={servicePath(s)}
              data-testid={`home-service-card-${s.slug}`}
              className="group rounded-2xl border border-white/10 bg-slate-900/60 p-6 transition-all duration-200 hover:border-amber-500/40 hover:bg-slate-900"
            >
              <s.icon className="h-8 w-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-amber-300 transition-colors">{s.name}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-4">{s.short}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-400">
                Learn more <ChevronRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      <section data-testid="cities-overview-section" className="relative py-20 border-y border-white/5">
        <div className="absolute inset-0">
          <img src={CITY_IMG} alt="Ontario city skyline protected by 1st Nations Security guard services" className="h-full w-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-950/85 to-slate-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">Ontario Service Areas</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-heading mb-4">
            Security Guards in {CITIES.length}+ Ontario Cities
          </h2>
          <p className="text-base text-slate-300 leading-relaxed max-w-3xl mb-10">
            Looking for security guards in Toronto, security guards in Thunder Bay, or a security company in Ottawa?
            We provide construction security across Ontario plus guard services in Mississauga, Brampton, Hamilton,
            London, Kitchener, Waterloo, Windsor, Sudbury, North Bay, Sault Ste. Marie, Timmins, Kenora and every
            region in between.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={cityPath(c)}
                data-testid={`home-city-link-${c.slug}`}
                className="flex items-center gap-2 rounded-lg border border-white/10 bg-slate-950/70 px-3.5 py-2.5 text-sm text-slate-300 transition-colors duration-200 hover:border-amber-500/40 hover:text-amber-300"
              >
                <MapPin className="h-3.5 w-3.5 text-amber-500 shrink-0" /> {c.name}
              </Link>
            ))}
          </div>
          <Link to="/ontario-service-areas" data-testid="home-all-cities-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300">
            View all Ontario service areas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section data-testid="heritage-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-3">Our Heritage</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white font-heading mb-5">
              A First Nations Security Company Built on Trust
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-4">
              1st Nations Security is a 100% Indigenous-owned security guard company with roots in Northwestern
              Ontario. The rising sun in our logo reflects who we are: a new day for professional security in
              Ontario, built on community, respect and reliability.
            </p>
            <p className="text-base text-slate-300 leading-relaxed mb-4">
              We protect First Nations communities and band facilities, mining and industrial operations, hospitals,
              construction sites, retail stores, condominiums and public events — and we hire and train local guards,
              including community members, wherever we work.
            </p>
            <Link to="/about-1st-nations-security" data-testid="heritage-about-link" className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300">
              Read our story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="rounded-2xl border border-amber-500/20 bg-slate-900/60 p-8">
            <h3 className="text-xl sm:text-2xl font-semibold text-amber-100 mb-6 font-heading">Why Ontario Chooses Us</h3>
            <ul className="space-y-4">
              {[
                "Licensed security guards under Ontario's PSISA",
                "24/7 dispatch and rapid alarm response",
                "Indigenous-owned with community-first values",
                "Coverage from Thunder Bay to Toronto to Windsor",
                "Detailed digital patrol and incident reporting",
                "Guards trained in de-escalation and first aid",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-slate-300">
                  <ShieldCheck className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section data-testid="home-faq-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white font-heading mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4 max-w-3xl">
          {homeFaqLd.mainEntity.map((f, i) => (
            <details key={i} data-testid={`home-faq-${i}`} className="group rounded-xl border border-white/10 bg-slate-900/60 p-5">
              <summary className="cursor-pointer text-base font-semibold text-white list-none flex items-center justify-between">
                {f.name}
                <ChevronRight className="h-4 w-4 text-amber-400 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm text-slate-300 leading-relaxed">{f.acceptedAnswer.text}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <CtaBand
          heading="Need Security Guards in Ontario?"
          sub="Talk to our 24/7 dispatch team about uniformed guards, mobile patrol, event security, fire watch or any security service — anywhere in Ontario."
        />
      </div>
    </div>
  );
}
